import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Table, Tag, Typography } from 'antd';
import {
  TeamOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import HrStatCard from '../components/HrStatCard';
import { useEmployees } from '../hooks/useEmployees';
import { useLeave } from '../hooks/useLeave';
import { useAttendance } from '../hooks/useAttendance';
import { usePayroll } from '../hooks/usePayroll';

const { Title } = Typography;

const HrDashboardView: React.FC = () => {
  const [onLeaveCount, setOnLeaveCount] = useState(0);
  const { employees, totalEmployees, fetchEmployees } = useEmployees();
  const { pendingCount, fetchPendingCount } = useLeave();
  const { countOnLeaveToday } = useAttendance();
  const { periods, fetchPeriods } = usePayroll();

  useEffect(() => {
    fetchEmployees();
    fetchPendingCount();
    fetchPeriods();
    countOnLeaveToday().then(setOnLeaveCount);
  }, []);

  const nextPayroll = periods.find((p) => p.status === 'DRAFT' || p.status === 'PENDING_APPROVAL');
  const recentHires = employees
    .slice()
    .sort((a, b) => new Date(b.hireDate).getTime() - new Date(a.hireDate).getTime())
    .slice(0, 5);

  const hireColumns = [
    { title: 'Employee No.', dataIndex: 'employeeNumber', key: 'employeeNumber' },
    { title: 'Name', key: 'name', render: (_: any, r: any) => `${r.firstName} ${r.lastName}` },
    { title: 'Department', dataIndex: 'departmentName', key: 'departmentName' },
    { title: 'Hire Date', dataIndex: 'hireDate', key: 'hireDate' },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (s: string) => (
        <Tag color={s === 'ACTIVE' ? 'green' : s === 'ON_LEAVE' ? 'orange' : 'default'}>{s}</Tag>
      ),
    },
  ];

  return (
    <div className="p-6">
      <Title level={3} style={{ marginBottom: 24 }}>HR Dashboard</Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <HrStatCard
            title="Active Employees"
            value={totalEmployees}
            prefix={<TeamOutlined />}
            valueStyle={{ color: '#1890ff' }}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <HrStatCard
            title="On Leave Today"
            value={onLeaveCount}
            prefix={<CalendarOutlined />}
            valueStyle={{ color: '#fa8c16' }}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <HrStatCard
            title="Pending Leave Approvals"
            value={pendingCount}
            prefix={<ClockCircleOutlined />}
            valueStyle={{ color: pendingCount > 0 ? '#f5222d' : '#52c41a' }}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <HrStatCard
            title="Next Payroll"
            value={nextPayroll?.periodName ?? 'None scheduled'}
            prefix={<DollarOutlined />}
            valueStyle={{ color: '#722ed1' }}
          />
        </Col>
      </Row>

      <Card title="Recent Hires">
        <Table
          dataSource={recentHires}
          columns={hireColumns}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};

export default HrDashboardView;
